import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Input2 } from 'v5/design-system/Input2';
import { Label } from 'v5/design-system/Label';
import { isLightColor, normalizeHex } from './ColorPicker.utils';

export type ColorPickerHexFieldProps = {
  value: string;
  onCommit: (hex: string) => void;
  /** Called after Enter commits a valid hex (e.g. to close the popover like a swatch click does). */
  onSubmit?: () => void;
  disabled?: boolean;
};

export function ColorPickerHexField({
  value,
  onCommit,
  onSubmit,
  disabled = false
}: ColorPickerHexFieldProps) {
  const { t } = useTranslation('patterns', { keyPrefix: 'ColorPicker' });
  const committedHex = normalizeHex(value) ?? '';
  const [draft, setDraft] = useState(committedHex || value.trim());

  useEffect(() => {
    setDraft(normalizeHex(value) || value.trim());
  }, [value]);

  const previewHex = normalizeHex(draft) ?? committedHex;

  const commit = () => {
    const next = normalizeHex(draft);
    if (!next) {
      setDraft(committedHex || value.trim());
      return false;
    }
    // Blur without an edit must not rewrite the stored value (e.g. `#f1f1f1` → `#F1F1F1`).
    if (next !== committedHex) {
      onCommit(next);
    } else {
      // value is unchanged, so the effect won't normalize the draft (`#f1f1f1` → `#F1F1F1`).
      setDraft(next);
    }
    return true;
  };

  return (
    <div className="space-y-2 border-t border-border-default pt-3">
      {previewHex ? (
        <div
          className="flex h-20 w-full items-center justify-center rounded-md border border-border-default font-mono text-sm font-medium"
          style={{
            backgroundColor: previewHex,
            color: isLightColor(previewHex) ? '#000000' : '#ffffff'
          }}
        >
          {previewHex}
        </div>
      ) : null}
      <div className="space-y-1.5">
        <Label className="text-xs text-fg-muted">{t('hexInputLabel')}</Label>
        <Input2
          value={draft}
          disabled={disabled}
          placeholder={t('hexInputPlaceholder')}
          className="font-mono"
          onChange={event => setDraft(event.target.value)}
          onBlur={commit}
          onKeyDown={event => {
            if (event.key === 'Enter') {
              event.preventDefault();
              if (commit()) {
                onSubmit?.();
              }
            }
          }}
        />
      </div>
    </div>
  );
}
